import VueRouter, { RouteConfig } from 'vue-router';
import store from '@/store';
import { DashboardDTO } from '@/data/dto/dashboards.dto';
import '@/store/modules/dashboard';

import SectionCategoriesKpiCards from '@/views/KPIs/SectionCategoriesKpiCards.vue';
import SectionKpiCards from '@/views/KPIs/SectionKpiCards.vue';
import EmptyRouterView from '@/components/EmptyRouterView.vue';
import IFramePage from '@/components/KPIs/IFramePage.vue';

// Constroí as rotas dinamicamente de forma hierarquica e recursiva.
const buildRouteConfig = (
  dashboard: DashboardDTO,
  allDashboards: DashboardDTO[]
): RouteConfig => {
  const meta = {
    id: dashboard.id,
    active: dashboard.active,
    isDynamicRoute: true,
    parentId: dashboard.parentId ?? null,
    icons: ['Bar_Chart_4_Bars'],
    labels: [dashboard.name],
    customColors: [dashboard.color],
    title: dashboard.name,
    subtitle: dashboard.subtitle,
    description: dashboard.description,
    requiresPermission: dashboard.permission,
    blocked: !dashboard.active,
    hiddenToNotAllowedUsers: false,
    hiddenOnNavigation: false,
    iFrameSrc: dashboard.url,
    userAllowed: dashboard.active,
    order: dashboard.order ?? 0,
    tagActive: dashboard.tagActive,
    tagText: dashboard.tagText,
    isAccessGloboButtonEnabled: dashboard.isAccessGloboButtonEnabled,
    accessGloboRuleName: dashboard.accessGloboRuleName,
    isSidebarMenuEnabled: dashboard.isSidebarMenuEnabled,
  };

  const childDashboards = allDashboards
    .filter(
      (dashboardToBeBuild) => dashboardToBeBuild.parentId === dashboard.id
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const path = dashboard.parentId ? dashboard.slug : `/${dashboard.slug}`;

  const routeName = `${dashboard.slug}-${dashboard.id}`;

    // Separa o nome da primeira palavra para usar como label principal
  // e o restante como secundário
  const isRootDashboard = dashboard.parentId == null;
  
  const defaultChildComponent = isRootDashboard
    ? SectionCategoriesKpiCards
    : SectionKpiCards;

  const customLabel = isRootDashboard
    ? [dashboard.name]
    : (() => {
        const parts = dashboard.name.replace(/\s+/g, ' ').trim().split(' ');
        return parts.length > 1 ? [parts[0], parts.slice(1).join(' ')] : parts;
      })();

 
  meta.labels = customLabel;
  
  if (!childDashboards.length) {

    if (dashboard.hiddenOnNavigation) {
      meta.hiddenToNotAllowedUsers = true;
      meta.hiddenOnNavigation = true;
    } else {
      meta.hiddenToNotAllowedUsers = false;
      meta.hiddenOnNavigation = false;
    }
    return {
      path,
      name: routeName,
      component: dashboard.url ? IFramePage : SectionKpiCards,
      meta: { ...meta, icons: ['view_week', 'timeline', 'table_view'] },
    };
  }




  // Rota com apenas um filho não precisa de nome pois não será navegável
  // pois o primeiro filho será a rota padrão.
  const name = childDashboards.length > 1 ? routeName : undefined;
  return {
    path,
    name,
    component: EmptyRouterView,
    meta,
    children: [
      {
        path: '',
        name: `${dashboard.slug}-${dashboard.id}`,
        component: defaultChildComponent,
        meta: { ...meta, hiddenOnNavigation: true },
      },
      ...childDashboards.map((child) => buildRouteConfig(child, allDashboards)),
    ],
  };
};

// Retorna true quando rotas novas foram registradas e a navegação precisa ser refeita.
export const middlewareAddDashboard = async (
  router: VueRouter
): Promise<boolean> => {
  const dashboardRoutesRegistered = store.state.dashboard.isDashboardRegistered;

  if (dashboardRoutesRegistered) {
    return false;
  }

  const dashboardList: DashboardDTO[] =
    (await store.dispatch('getAllDashboards')) || [];
  if (!dashboardList.length) {
    await store.dispatch('setDashboardRegistered', true);
    return false;
  }

  const generatedRootdRoutes = dashboardList
    .filter((dashboard) => dashboard.parentId == null) // apenas dashboards raiz
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((rootDashboard) => buildRouteConfig(rootDashboard, dashboardList));

  const rootRoutes = generatedRootdRoutes.filter(
    (route) => route.meta?.parentId == null
  );

  // Adiciona rotas no VueRoter
  rootRoutes.forEach((route) => {
    router.addRoute(route);
  });
  // Adiciona rotas na Store
  await store.dispatch('addDashboardRoutesToCurrentUser', generatedRootdRoutes);

  await store.dispatch('setDashboardRegistered', true);
  return true;
};
